import React, { useState, memo, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemText,
  Typography
} from '@mui/material';
import { Functions as FunctionsIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import MathRenderer from '../common/MathRenderer';

// Static array - never changes, defined outside component
const MATH_FORMULAS = [
  { label: 'Fraction', latex: '\\frac{a}{b}' },
  { label: 'Square Root', latex: '\\sqrt{x}' },
  { label: 'Power', latex: 'x^{n}' },
  { label: 'Pi', latex: '\\pi' },
  { label: 'Theta', latex: '\\theta' },
  { label: 'Plus/Minus', latex: '\\pm' },
  { label: 'Not Equal', latex: '\\neq' },
  { label: 'Less Equal', latex: '\\leq' },
  { label: 'Quadratic', latex: 'ax^2 + bx + c = 0' },
  { label: 'Linear', latex: 'y = mx + b' },
  { label: 'Derivative', latex: '\\frac{d}{dx}' },
  { label: 'Integral', latex: '\\int_{a}^{b} f(x) dx' },
  { label: 'Area Circle', latex: 'A = \\pi r^2' },
  { label: 'Pythagoras', latex: 'a^2 + b^2 = c^2' },
];

// Ultra-optimized MathEditor with integrated preview functionality
const MathEditor = memo(({ 
  value = '', 
  onChange, 
  label = "Question Text",
  placeholder = "Enter your question text here...",
  multiline = true,
  minRows = 3,
  fullWidth = true,
  error = false,
  helperText = "Use the math button (∑) to insert mathematical formulas. For math: type $formula$ or $$formula$$. For currency: type $5, $10.50, etc.",
  showPreview = true // Enable preview by default
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const safeValue = value != null && typeof value === 'string' ? value : '';
  const [localValue, setLocalValue] = useState(safeValue);
  const [previewVisible, setPreviewVisible] = useState(false);
  const debounceRef = useRef(null);
  const lastValueRef = useRef(safeValue);

  // Update local value when prop changes (but not on every keystroke)
  useEffect(() => {
    const next = value != null && typeof value === 'string' ? value : '';
    if (next !== lastValueRef.current) {
      setLocalValue(next);
      lastValueRef.current = next;
    }
  }, [value]);

  // Debounced onChange to prevent excessive parent updates
  const debouncedOnChange = useCallback((newValue) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    debounceRef.current = setTimeout(() => {
      if (newValue !== lastValueRef.current) {
        lastValueRef.current = newValue;
        onChange(newValue);
      }
    }, 150); // 150ms debounce for better performance
  }, [onChange]);

  // Handle math formula insertion
  const insertFormula = useCallback((latex) => {
    // Check if the latex already has delimiters
    const hasDelimiters = latex.startsWith('$') && latex.endsWith('$');
    const hasDisplayDelimiters = latex.startsWith('$$') && latex.endsWith('$$');
    
    let mathTag;
    if (hasDelimiters || hasDisplayDelimiters) {
      // If it already has delimiters, use as-is
      mathTag = latex;
    } else {
      // Add inline math delimiters only if not present
      mathTag = `$${latex}$`;
    }
    
    const currentValue = String(localValue ?? '');
    
    // Check if we're in the middle of typing a formula
    const lastChar = currentValue.slice(-1);
    const needsSpace = lastChar && !lastChar.match(/\s/) && !lastChar.match(/[\(\[\{]/);
    
    const newValue = currentValue + (needsSpace ? ' ' : '') + mathTag;
    
    setLocalValue(newValue);
    debouncedOnChange(newValue);
    setAnchorEl(null);
  }, [localValue, debouncedOnChange]);

  // Smart math detection that differentiates between currency and formulas
  const detectMathFormulas = useCallback((text) => {
    if (!text) return { hasMath: false, mathRanges: [], cleanedText: text };
    
    const mathRanges = [];
    let cleanedText = text;
    
    // Pattern to detect math formulas: $ followed by LaTeX commands
    const mathPattern = /\$([^$\s]+(?:\s+[^$\s]+)*?)\$/g;
    let match;
    
    while ((match = mathPattern.exec(text)) !== null) {
      const fullMatch = match[0];
      const content = match[1];
      
      // Check if this looks like a math formula (contains LaTeX commands)
      const hasLatexCommands = /\\[a-zA-Z]+|\\[{}[\]]|\\[^a-zA-Z]/.test(content);
      
      // Check if this looks like currency (just numbers, maybe with decimal/commas)
      const looksLikeCurrency = /^[\d,]+(\.\d+)?$/.test(content);
      
      // Check if this looks like a simple variable or expression
      const looksLikeMath = /[a-zA-Z]/.test(content) || hasLatexCommands;
      
      // Check if this looks like a simple number (not currency)
      const looksLikeNumber = /^[\d.]+$/.test(content) && !looksLikeCurrency;
      
      if (looksLikeMath && !looksLikeCurrency) {
        // This is likely a math formula
        mathRanges.push({
          start: match.index,
          end: match.index + fullMatch.length,
          content: content,
          type: 'math'
        });
      } else if (looksLikeCurrency) {
        // Currency: leave as-is so preview shows $4, $5, etc. MathRenderer treats non-LaTeX $...$ as literal.
        // Do not replace with \$, which was stripping $ from the preview.
      } else if (looksLikeNumber) {
        // This might be a number in math context - check surrounding context
        const beforeMatch = text.substring(Math.max(0, match.index - 10), match.index);
        const afterMatch = text.substring(match.index + fullMatch.length, Math.min(text.length, match.index + fullMatch.length + 10));
        
        // If surrounded by math context, treat as math; otherwise as currency
        const hasMathContext = /[+\-*/=<>()]/.test(beforeMatch + afterMatch);
        
        if (hasMathContext) {
          mathRanges.push({
            start: match.index,
            end: match.index + fullMatch.length,
            content: content,
            type: 'math'
          });
        } else {
          // Treat as currency: leave as-is so preview shows $; MathRenderer shows literal.
        }
      }
    }
    
    return {
      hasMath: mathRanges.length > 0,
      mathRanges: mathRanges,
      cleanedText: cleanedText
    };
  }, []);

  // Enhanced math delimiter cleanup with smart detection
  const cleanupMathDelimiters = useCallback((text) => {
    if (!text) return text;
    
    // First, detect and handle math vs currency
    const mathInfo = detectMathFormulas(text);
    
    let cleaned = mathInfo.cleanedText;
    
    // Remove triple $ that might have been added incorrectly
    cleaned = cleaned.replace(/\$\$\$\$/g, '$$');
    
    // Remove double $$ that might have been added incorrectly (but preserve valid $$)
    cleaned = cleaned.replace(/\$\$\$/g, '$$');
    
    // Fix common delimiter issues
    cleaned = cleaned.replace(/\$\s*\$/g, '$$'); // $ $ becomes $$
    cleaned = cleaned.replace(/\$\s*\\/g, '$\\'); // $ \ becomes $\
    
    return cleaned;
  }, [detectMathFormulas]);

  // Enhanced local change handler with delimiter cleanup
  const handleLocalChange = useCallback((e) => {
    const newValue = e.target.value;
    const cleanedValue = cleanupMathDelimiters(newValue);
    
    setLocalValue(cleanedValue);
    
    // Debounce the parent update
    debouncedOnChange(cleanedValue);
  }, [debouncedOnChange, cleanupMathDelimiters]);

  // Menu handlers
  const handleClick = useCallback((event) => setAnchorEl(event.currentTarget), []);
  const handleClose = useCallback(() => setAnchorEl(null), []);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
        <TextField
          label={label}
          value={typeof localValue === 'string' ? localValue : String(localValue ?? '')}
          onChange={handleLocalChange}
          placeholder={placeholder}
          multiline={multiline}
          minRows={minRows}
          fullWidth={fullWidth}
          error={error}
          helperText={helperText}
          sx={{ flex: '1 1 240px', minWidth: 0 }}
          inputProps={{
            'aria-label': label,
            'aria-describedby': helperText ? `${label}-helper-text` : undefined
          }}
        />
        
        {/* Math Formula Button */}
        <Tooltip title="Insert Math Formula">
          <IconButton
            onClick={handleClick}
            aria-label="Insert mathematical formula"
            aria-haspopup="true"
            aria-expanded={Boolean(anchorEl)}
            sx={{
              backgroundColor: 'primary.main',
              color: 'white',
              flexShrink: 0,
              '&:hover': { backgroundColor: 'primary.dark' }
            }}
          >
            <FunctionsIcon />
          </IconButton>
        </Tooltip>
        
        {/* Preview Button - only show if preview is enabled */}
        {showPreview && (
          <Tooltip title={previewVisible ? "Hide Preview" : "Show Preview"}>
            <IconButton
              onClick={() => setPreviewVisible(!previewVisible)}
              aria-label={previewVisible ? "Hide preview" : "Show preview"}
              color={previewVisible ? "primary" : "default"}
              sx={{
                backgroundColor: previewVisible ? 'primary.main' : 'transparent',
                color: previewVisible ? 'white' : 'inherit',
                flexShrink: 0,
                '&:hover': {
                  backgroundColor: previewVisible ? 'primary.dark' : 'action.hover'
                }
              }}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Preview Section */}
      {showPreview && previewVisible && String(localValue ?? '').trim() && (
        <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, backgroundColor: 'grey.50' }}>
          <Typography variant="subtitle2" gutterBottom>
            Preview:
          </Typography>
          <MathRenderer content={String(localValue ?? '')} />
          
          {/* Show intelligent math detection info */}
          {(() => {
            const strVal = String(localValue ?? '');
            const mathInfo = detectMathFormulas(strVal);
            if (mathInfo.hasMath || strVal.includes('$')) {
              return (
                <Box sx={{ mt: 1, p: 1, backgroundColor: 'info.light', borderRadius: 1 }}>
                  <Typography variant="caption" color="info.contrastText" sx={{ display: 'block', mb: 0.5 }}>
                    💡 Smart Detection Results:
                  </Typography>
                  {mathInfo.mathRanges.length > 0 && (
                    <Typography variant="caption" color="info.contrastText" sx={{ display: 'block' }}>
                      ✅ Math formulas detected: {mathInfo.mathRanges.length} formula(s)
                    </Typography>
                  )}
                  {strVal.includes('$') && !mathInfo.hasMath && (
                    <Typography variant="caption" color="info.contrastText" sx={{ display: 'block' }}>
                      💰 Currency detected: $ symbols treated as dollar signs
                    </Typography>
                  )}
                  <Typography variant="caption" color="info.contrastText" sx={{ display: 'block', mt: 0.5 }}>
                    💡 Use $formula$ for math or type $5 for currency
                  </Typography>
                </Box>
              );
            }
            return null;
          })()}
        </Box>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{ sx: { maxHeight: 400, width: 300 } }}
        role="menu"
        aria-label="Mathematical formulas"
      >
        {MATH_FORMULAS.map((item, index) => (
          <MenuItem 
            key={index} 
            onClick={() => insertFormula(item.latex)}
            role="menuitem"
            aria-label={`Insert ${item.label} formula: ${item.latex}`}
          >
            <ListItemText 
              primary={item.label}
              secondary={item.latex}
              primaryTypographyProps={{ variant: 'body2' }}
              secondaryTypographyProps={{ variant: 'caption', fontFamily: 'monospace' }}
            />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
});

MathEditor.displayName = 'MathEditor';

export default MathEditor;
